import { makeChirpAvroCallWithFetch, ChirpInternalError, spreadPropertiesIntoError } from './chirp-avro';
import streamFn from './streamClient';
import { resolveLocalRequest } from './localRequestUtils';
import { consumeEarlyStreamEntry, getEarlyStreamEntryFor } from './chirp-streaming-quick-fetch';
import { createStreamHandlerBase } from './streamHandlerBase';

/**
 * Creates a handler for streaming RPC responses.
 *
 * The returned StreamHandler extends Promise and supports both callback and Promise patterns:
 * - Use .onConnected() to be notified when the connection is established
 * - Use .onMessage() to handle data chunks (including domain errors)
 * - Use .then() to handle stream completion
 * - Use .catch() to handle transport errors
 * - Use .abort() to stop the stream
 *
 * @param details The details of the streaming RPC
 * @param inputs The inputs for the RPC call
 * @param fetchClient The fetch client to use for making requests
 * @returns A StreamHandler that allows handling data and supports Promise patterns
 */
export function createStreamHandler(details, inputs, fetchClient, observer) {
  const chirpFetch = fetchClient;
  const {
    serviceName
  } = details;
  return createStreamHandlerBase({
    syncStackError: new Error(`CHIRP streaming RPC failed for ${details.rpcName}`),
    observer: observer ? data => observer(data) : undefined,
    interceptData: data => {
      const typedData = data;
      if (typedData.type === 'internalError') {
        Object.assign(typedData.internalError, {
          serviceName
        });
        const chirpInternalError = new ChirpInternalError('CHIRP RPC failed with a CHIRP internal error', {
          cause: typedData.internalError
        });
        spreadPropertiesIntoError(chirpInternalError, typedData.internalError);
        return chirpInternalError;
      }
    },
    mapError: error => {
      Object.assign(error, {
        serviceName
      });
      return new ChirpInternalError('CHIRP RPC failed with a CHIRP internal error', {
        cause: error
      });
    },
    run: async (signal, onConnected, onProgress, onError, onClose) => {
      const localService = await resolveLocalRequest(serviceName, inputs === null || inputs === void 0 ? void 0 : inputs.explicitLocalWorkspace);
      const shouldMakeLocalRequest = localService !== null;
      const makeNormalFetch = () => makeChirpAvroCallWithFetch(chirpFetch, details, inputs, shouldMakeLocalRequest, signal, localService === null || localService === void 0 ? void 0 : localService.workspace);
      const earlyEntry = getEarlyStreamEntryFor(details, inputs);
      let response;
      if (earlyEntry) {
        // Detach early controller, we'll manage cancellation locally; do not abort existing response
        consumeEarlyStreamEntry(details, inputs);
        try {
          response = await earlyEntry.response;
          if (!response.ok) {
            var _response$body;
            (_response$body = response.body) === null || _response$body === void 0 || _response$body.cancel().catch(() => {});
            response = await makeNormalFetch();
          }
        } catch (_unused) {
          response = await makeNormalFetch();
        }
      } else {
        response = await makeNormalFetch();
      }
      onConnected();
      await streamFn(response, {
        onProgress,
        onError,
        onClose,
        signal
      });
    }
  });
}